import { Injectable, inject } from '@angular/core';
import { AuthApiService } from '@api-new/authservice';
import { NotificationApiService } from '@api-new/notificationservice';
import { AppState } from '@capacitor/app';
import { Device } from '@capacitor/device';
import { PushNotifications } from '@capacitor/push-notifications';
import { environment } from '@environments/environment';
import { Store } from '@ngrx/store';
import { paths } from '@platform/paths';
import { loginSuccess } from '@shared/store/auth/auth.action';
import { firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class NotificationsService {
  private readonly notificationApiService = inject(NotificationApiService);
  private readonly authApiService = inject(AuthApiService);
  private notificationInitialized = false;
  private readonly globalStore = inject(Store<AppState>);
  private readonly DISABLE_PUSH_NOTIFICATIONS = true;

  constructor() {
    if (this.DISABLE_PUSH_NOTIFICATIONS) return;
    this.listenTuPushNotifications();
  }

  initPushNotifications(userId: string): void {
    if (this.DISABLE_PUSH_NOTIFICATIONS) return;
    if (!userId || !environment.mobileApp?.isMobileApp || this.notificationInitialized) return;

    this.registerNotifications();
    this.addPushNotificationListeners();
    this.notificationInitialized = true;
  }

  registerNotifications = async (): Promise<void> => {
    let permStatus = await PushNotifications.checkPermissions();

    if (permStatus.receive === 'prompt') {
      permStatus = await PushNotifications.requestPermissions();
    }

    await PushNotifications.register();
  };

  addPushNotificationListeners = async (): Promise<void> => {
    await PushNotifications.addListener('registration', (token) => {
      this.registerPushDevice(token.value);
    });

    await PushNotifications.addListener('registrationError', (err) => {
      console.error('Registration error: ', err.error);
    });
  };

  async listenTuPushNotifications(): Promise<void> {
    if (environment.mobileApp?.isMobileApp)
      await PushNotifications.addListener('pushNotificationActionPerformed', async (notification) => {
        void this.reactOnNotificationOpen(notification);
      });
  }

  registerPushDevice = async (pushNotificationToken: string): Promise<void> => {
    const deviceId = await Device.getId();
    await firstValueFrom(
      this.notificationApiService.HTTP_CP_SetPushNotificationToken({ deviceId: deviceId.identifier, pushNotificationToken }),
    );
  };

  unregisterToken = async (): Promise<void> => {
    if (this.DISABLE_PUSH_NOTIFICATIONS) return;
    await PushNotifications?.unregister();
  };

  async reactOnNotificationOpen(notification): Promise<void> {
    if (!notification.notification?.data) return;

    const email = notification.notification.data.clientUserEmail;
    const verificationCode = notification.notification.data.signInCode;
    const companyId = notification.notification.data.companyId;
    const redirectUrl = `/${paths.PLATFORM}/${paths.platform.PROPERTIES}`;

    const signInResponse = await firstValueFrom(
      this.authApiService.HTTP_CP_SignIn_WithCode({
        email: email,
        code: verificationCode,
        profileCompanyId: companyId,
        signInCompanyId: companyId,
      }),
    );
    this.globalStore.dispatch(
      loginSuccess({
        signInResponse,
        link: redirectUrl,
      }),
    );
  }
}
